/**
 * 메타(페이스북) 픽셀 전용 도우미 (2026-09-22)
 *
 * 직접 부르지 말고 공통 진입점 utils/tracking 의 initTracking·trackPageView·trackLead·trackContact 를 쓴다
 * (GA4·구글애즈·카카오·네이버 전환과 한 번에 전송).
 *
 * 사양(광고 엔진 확정): 이벤트 이름 대소문자 정확히 — PageView · Lead · Contact.
 * 파라미터 content_name · content_ids · content_category('apartment'|'officetel') · contact_method.
 * value/currency 금지. Lead 는 서버 CAPI 와 같은 eventID 로 중복 제거. 이중 init 금지.
 */

type AnyWindow = Window & { [key: string]: any };
const w = (): AnyWindow => window as unknown as AnyWindow;

export const META_PIXEL_SRC = 'https://connect.facebook.net/en_US/fbevents.js';
export const META_PIXEL_ID_PATTERN = /^\d{5,20}$/;

export interface MetaContentParams {
  content_name: string;
  content_category: 'apartment' | 'officetel';
  contact_method: 'form' | 'phone' | 'kakao';
  content_ids?: string[];
}

const hasFbq = (): boolean => typeof w().fbq === 'function';

/** 이 픽셀 ID 가 우리 로더 밖(관리자 HEAD 스크립트 등)에서 이미 init 됐는지 */
const initializedElsewhere = (id: string): boolean =>
  hasFbq() &&
  Array.from(document.scripts).some(
    (s) => !s.hasAttribute('data-vn-tracking') && (s.textContent || '').includes(id),
  );

/** 픽셀 로드 + init + 최초 PageView (같은 ID 두 번 init 하지 않음) */
export function initMetaPixel(id: string): void {
  if (!META_PIXEL_ID_PATTERN.test(id)) return;
  if (initializedElsewhere(id)) return;
  const win = w();
  if (!hasFbq()) {
    // 메타 공식 스텁과 동일 동작: fbevents.js 로드 전 호출은 queue 에 쌓인다
    const n: any = function fbqStub(...args: any[]) {
      if (n.callMethod) n.callMethod(...args);
      else n.queue.push(args);
    };
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    win.fbq = n;
    if (!win._fbq) win._fbq = n;
    const exists = Array.from(document.scripts).some((s) => (s.src || '').includes('connect.facebook.net/en_US/fbevents.js'));
    if (!exists) {
      const s = document.createElement('script');
      s.async = true;
      s.src = META_PIXEL_SRC;
      s.setAttribute('data-vn-tracking', 'true');
      document.head.appendChild(s);
    }
  }
  win.fbq('init', id);
  // 다른 픽셀이 이미 있으면 그 픽셀엔 PageView 가 두 번 가지 않도록 trackSingle
  win.fbq('trackSingle', id, 'PageView');
}

export function metaPageView(): void {
  if (hasFbq()) w().fbq('track', 'PageView');
}

export function metaLead(params: MetaContentParams, eventId: string): void {
  if (hasFbq()) w().fbq('track', 'Lead', params, { eventID: eventId });
}

export function metaContact(params: MetaContentParams, eventId: string): void {
  if (hasFbq()) w().fbq('track', 'Contact', params, { eventID: eventId });
}
